import dayjs from "dayjs";
import { parseServerUtc } from "./lotClock";

/**
 * The rules and wording behind the Broadcasting Center (MG-014 / AD-013, #167 / #168): the history
 * list, the Send Broadcast page with its recipient picker, and Broadcast Details. Kept pure so they
 * can be tested on their own.
 *
 * The server decides who may be emailed (players subscribed to and approved by the Manager) and
 * applies the same limits; these rules only keep the page in step with it.
 */

export const BROADCASTS_PATH = "/SuperAdmin/BroadcastingCenter";
export const SEND_BROADCAST_PATH = `${BROADCASTS_PATH}/Send`;

/** Broadcast Details of one broadcast. */
export const broadcastDetailsPath = (id) => `${BROADCASTS_PATH}/${id}`;

/** Whether a path is one of the Broadcasting Center's pages, for the sidebar's highlight. */
export const isBroadcastPath = (path) => String(path || "").startsWith(BROADCASTS_PATH);

export const SUBJECT_MAX_LENGTH = 200;
export const MESSAGE_MAX_LENGTH = 5000;

/** The most players one broadcast can go to; the server refuses more. */
export const RECIPIENT_LIMIT = 100;

/** Selected players shown as chips under the Recipients field before "+X more". */
export const CHIP_LIMIT = 6;

/** How often a page re-reads a broadcast whose emails are still going out. */
export const PROGRESS_POLL_MS = 5000;

/** A recipient's status, as tblBroadcastRecipient stores it. */
export const RECIPIENT_STATUS = {
  PENDING: "Pending",
  SENDING: "Sending",
  SENT: "Sent",
  FAILED: "Failed",
};

/** The alerts that lead with a bold sentence (BroadcastAlert's `title`), as in the design's State 11. */
export const NO_REPLY_ALERT = {
  title: "We couldn't confirm the broadcast was sent.",
  text: "Check Broadcasting Center before sending again.",
};

export const GENERAL_FAILURE_ALERT = {
  title: "The broadcast wasn't sent.",
  text: "The email service is unavailable right now and no emails went out. Please try again later.",
};

export const PARTIAL_STOP_ALERT = {
  title: "Some emails weren't sent.",
  text: "The email service stopped partway through. Please try again later for the players marked Failed.",
};

export const LIST_LOAD_FAILED_MESSAGE = "The broadcasts could not be loaded. Reload the page to try again.";

export const DETAILS_LOAD_FAILED_MESSAGE = "This broadcast could not be loaded. Reload the page to try again.";

export const STOPPED_UPDATING_MESSAGE =
  "This page stopped updating because the server couldn't be reached. Reload the page to see the latest status.";

/**
 * Whether a wrapper's result is no answer at all: no reply (undefined) or a 5xx - a database fault's
 * ErrorDetails body, or a gateway's page. apiClient has already said so where it needed to. Such a
 * result says nothing about the broadcast: not that it is missing, not that the list is empty, and
 * for Send not that nothing was saved.
 *
 * @param {object|undefined} res What an auth-service wrapper resolved to.
 * @returns {boolean}
 */
export const isServerFailure = (res) => !res || res.status >= 500;

/** #168 AC7, word for word. */
export const ADMIN_NO_PLAYERS_MESSAGE =
  "Your Manager doesn't have any approved players yet. Ask your Manager to approve player subscriptions to send a broadcast.";

/** "1 player" / "N players". */
export const playersLabel = (count) => (count === 1 ? "1 player" : `${count} players`);

/**
 * The Send Broadcast hint for the approved players who turned the team's emails off. They are not
 * listed (Broadcast/GetRecipients leaves them out), so the page says how many there are; null when
 * there are none.
 *
 * @param {number} count `optedOutCount` from Broadcast/GetRecipients.
 * @returns {string|null}
 */
export const optedOutLabel = (count) => {
  if (!count) return null;
  return count === 1
    ? "1 approved player has turned off emails and isn't listed."
    : `${count} approved players have turned off emails and aren't listed.`;
};

/**
 * The Send Broadcast hint for the approved players who have not verified their email. No email
 * reaches them until they do, so they are not listed either; null when there are none.
 *
 * @param {number} count `unverifiedCount` from Broadcast/GetRecipients.
 * @returns {string|null}
 */
export const unverifiedLabel = (count) => {
  if (!count) return null;
  return count === 1
    ? "1 approved player hasn't verified their email and isn't listed."
    : `${count} approved players haven't verified their email and aren't listed.`;
};

/**
 * The Send Broadcast empty state when no approved player can be emailed: every one has turned the
 * emails off or, when `unverifiedCount` is set, not verified their email.
 *
 * @param {boolean} isManager
 * @param {number} [unverifiedCount] `unverifiedCount` from Broadcast/GetRecipients.
 * @returns {string}
 */
export const allOptedOutMessage = (isManager, unverifiedCount = 0) => {
  const players = isManager ? "All your approved players" : "All your Manager's approved players";
  const reason = unverifiedCount > 0 ? "have turned off emails or haven't verified their email" : "have turned off emails";
  return `${players} ${reason}, so there's nobody to send a broadcast to.`;
};

/**
 * Broadcast Details' line for the players never emailed because they turned the Manager's emails
 * off, or had not verified their email. They are not listed and not in the recipient count; null
 * when there are none.
 *
 * @param {number} count `skippedCount` from Broadcast/GetBroadcastById.
 * @returns {string|null}
 */
export const skippedLabel = (count) => {
  if (!count) return null;
  return `${playersLabel(count)} skipped: they turned off emails or haven't verified their email.`;
};

/** The pill next to Send: "Sending to N players", or that nobody is selected yet. */
export const summaryLabel = (count) =>
  count > 0 ? `Sending to ${playersLabel(count)}` : "No recipients selected";

const normalize = (text) => String(text || "").trim().toLowerCase();

/**
 * Whether a player matches the picker's search: a partial, case-insensitive match on the name or the
 * email (AC2). An empty search matches everyone.
 *
 * @param {{playerName?: string, emailId?: string}} player A player from Broadcast/GetRecipients.
 * @param {string} term What was typed.
 * @returns {boolean}
 */
export const matchesSearch = (player, term) => {
  const needle = normalize(term);
  if (!needle) return true;
  return (
    normalize(player?.playerName).includes(needle) || normalize(player?.emailId).includes(needle)
  );
};

/** The players the picker lists for a search, in the order given. */
export const filterPlayers = (players, term) =>
  (players || []).filter((player) => matchesSearch(player, term));

/**
 * Splits a text into the parts to show plain and the parts to highlight: every case-insensitive
 * occurrence of the search.
 *
 * @param {string} text A name or an email.
 * @param {string} term What was typed.
 * @returns {{text: string, match: boolean}[]}
 */
export const highlightSegments = (text, term) => {
  const value = String(text || "");
  const needle = normalize(term);
  if (!needle || !value) return value ? [{ text: value, match: false }] : [];

  const segments = [];
  const haystack = value.toLowerCase();
  let from = 0;
  let at = haystack.indexOf(needle, from);
  while (at !== -1) {
    if (at > from) segments.push({ text: value.slice(from, at), match: false });
    segments.push({ text: value.slice(at, at + needle.length), match: true });
    from = at + needle.length;
    at = haystack.indexOf(needle, from);
  }
  if (from < value.length) segments.push({ text: value.slice(from), match: false });
  return segments;
};

/**
 * "Select All" / "Select All results": adds the listed players to the selection, in list order, until
 * it holds RECIPIENT_LIMIT. Players selected earlier who are not listed stay selected (AC2).
 *
 * @param {Set<number>} selected The selected player ids.
 * @param {{playerId: number}[]} listed The players the picker lists now.
 * @param {number} [limit]
 * @returns {Set<number>}
 */
export const selectResults = (selected, listed, limit = RECIPIENT_LIMIT) => {
  const next = new Set(selected);
  for (const player of listed || []) {
    if (next.size >= limit) break;
    next.add(player.playerId);
  }
  return next;
};

/** "Deselect All" / "Deselect All results": removes only the listed players from the selection. */
export const deselectResults = (selected, listed) => {
  const next = new Set(selected);
  (listed || []).forEach((player) => next.delete(player.playerId));
  return next;
};

/** Ticks or unticks one player. A tick past RECIPIENT_LIMIT changes nothing. */
export const toggleSelected = (selected, playerId, limit = RECIPIENT_LIMIT) => {
  const next = new Set(selected);
  if (next.has(playerId)) next.delete(playerId);
  else if (next.size < limit) next.add(playerId);
  return next;
};

/** Whether the selection is full: no other player can be ticked. */
export const atRecipientLimit = (selected, limit = RECIPIENT_LIMIT) => selected.size >= limit;

/**
 * The picker's two bulk links: worded for a search, and each disabled when it would change nothing
 * (AC2) - Select All also once the selection is full.
 *
 * @param {Set<number>} selected The selected player ids.
 * @param {{playerId: number}[]} listed The players the picker lists now.
 * @param {string} term What was typed.
 * @param {number} [limit]
 * @returns {{selectLabel: string, deselectLabel: string, selectDisabled: boolean, deselectDisabled: boolean}}
 */
export const bulkLinks = (selected, listed, term, limit = RECIPIENT_LIMIT) => {
  const searching = Boolean(normalize(term));
  const items = listed || [];
  const selectedCount = items.filter((player) => selected.has(player.playerId)).length;
  return {
    selectLabel: searching ? "Select All results" : "Select All",
    deselectLabel: searching ? "Deselect All results" : "Deselect All",
    selectDisabled: items.length === 0 || selectedCount === items.length || atRecipientLimit(selected, limit),
    deselectDisabled: selectedCount === 0,
  };
};

/** The line above the picker's list: "N players", or "N of M players match" while searching. */
export const listedLabel = (listedCount, totalCount, term) =>
  normalize(term) ? `${listedCount} of ${totalCount} players match` : playersLabel(totalCount);

/**
 * The text on the closed Recipients field; null shows the placeholder.
 *
 * @param {number} selectedCount Players selected.
 * @param {number} totalCount Players available.
 * @returns {string|null}
 */
export const triggerLabel = (selectedCount, totalCount) => {
  if (!selectedCount) return null;
  if (selectedCount === totalCount) return `All players (${totalCount})`;
  return `${playersLabel(selectedCount)} selected`;
};

/**
 * The selected players in the order the picker lists them, and how many chips that is, for the
 * chips under the Recipients field: the first CHIP_LIMIT, then "+X more".
 *
 * @param {{playerId: number}[]} players Every available player.
 * @param {Set<number>} selected The selected player ids.
 * @param {number} [limit]
 * @returns {{shown: object[], more: number}}
 */
export const chipsView = (players, selected, limit = CHIP_LIMIT) => {
  const chosen = (players || []).filter((player) => selected.has(player.playerId));
  return { shown: chosen.slice(0, limit), more: Math.max(0, chosen.length - limit) };
};

/**
 * The inline errors of the Send Broadcast form, one per field still wrong (AC3). Empty when the
 * broadcast can be sent.
 *
 * @param {{selectedCount: number, subject: string, message: string}} form
 * @returns {{players?: string, subject?: string, message?: string}}
 */
export const validateBroadcast = ({ selectedCount, subject, message }) => {
  const errors = {};
  if (!selectedCount) errors.players = "Select at least one player.";
  else if (selectedCount > RECIPIENT_LIMIT)
    errors.players = `A broadcast can go to at most ${RECIPIENT_LIMIT} players.`;

  const cleanSubject = String(subject || "").trim();
  if (!cleanSubject) errors.subject = "Enter a subject.";
  else if (cleanSubject.length > SUBJECT_MAX_LENGTH)
    errors.subject = `The subject can be at most ${SUBJECT_MAX_LENGTH} characters.`;

  const text = String(message || "");
  if (!text.trim()) errors.message = "Enter a message.";
  else if (text.length > MESSAGE_MAX_LENGTH) errors.message = "The message can be at most 5,000 characters.";

  return errors;
};

/** Whether a validation result has any error. */
export const hasErrors = (errors) => Object.keys(errors || {}).length > 0;

/**
 * The history's Recipient Emails cell: the first five addresses the server sent, and how many
 * recipients that leaves for "and X more" (AC6).
 *
 * @param {string|null} firstEmails Comma-separated, from Broadcast/GetBroadcasts.
 * @param {number} recipientCount Every recipient of the broadcast.
 * @returns {{emails: string, more: number}}
 */
export const emailsCell = (firstEmails, recipientCount) => {
  const emails = String(firstEmails || "")
    .split(",")
    .map((email) => email.trim())
    .filter(Boolean);
  return { emails: emails.join(", "), more: Math.max(0, (recipientCount || 0) - emails.length) };
};

/** Whether the background sender is still working through a broadcast. */
export const isInProgress = (broadcast) => (broadcast?.pendingCount || 0) > 0;

/**
 * The tag next to a history row's recipient count: grey while emails are going out, red once some
 * failed, none otherwise.
 *
 * @param {{pendingCount?: number, failedCount?: number}} row A row of Broadcast/GetBroadcasts.
 * @returns {{kind: "sending"|"failed", label: string}|null}
 */
export const listStatusTag = (row) => {
  if (isInProgress(row)) return { kind: "sending", label: "Sending…" };
  if ((row?.failedCount || 0) > 0) return { kind: "failed", label: `${row.failedCount} failed` };
  return null;
};

/** How Broadcast Details shows one recipient's status. */
export const recipientStatusView = (status) => {
  switch (status) {
    case RECIPIENT_STATUS.SENT:
      return { kind: "sent", label: "Sent" };
    case RECIPIENT_STATUS.FAILED:
      return { kind: "failed", label: "Failed" };
    default:
      return { kind: "sending", label: "Sending…" };
  }
};

/**
 * Whether Broadcast Details shows "the broadcast wasn't sent": the email service failed and not one
 * email went out.
 */
export const generalFailure = (details) =>
  Boolean(details?.hasGeneralError) && !details?.sentCount && !isInProgress(details);

/** Whether the email service failed after some emails had already gone out. */
export const partialStop = (details) =>
  Boolean(details?.hasGeneralError) && (details?.sentCount || 0) > 0 && !isInProgress(details);

/**
 * "10/09/2026, 6:42 PM" in the viewer's time zone. The server marks its dates UTC (a trailing Z);
 * one without a zone is taken as UTC too, as every other server date is (parseServerUtc).
 *
 * @param {string|null} value An ISO date from the API.
 * @returns {string}
 */
export const formatSentAt = (value) => {
  if (!value) return "";
  return dayjs(parseServerUtc(value)).format("MM/DD/YYYY, h:mm A");
};
